import { useDebounce, useToast } from "../../../shared/hooks";
import { ToastContent } from "../../../shared/components/ToastContent"; // Adjust the path if necessary
import { FinancialService, INfse } from "../../../shared/services/api/financial/FinancialService";
import { returnMessageError } from "../../../shared/utils/returnMessageError";
import { useState } from "react";
import { NfseUI } from "../types/types";

export function useHandleNfse() {
	const toastHandler = useToast();
	const { debounce } = useDebounce();
	const [isLoading, setIsLoading] = useState(false);

	const handleDownloadNfsePDF = (row: any) => {
		setIsLoading(true);
		debounce(() => {
			const call =
				row?.nfse_gateway === "GRIDWEBTI"
					? FinancialService.getNfsePDFGridWebti(row?.nfse_id, row?.nfse_nfse_urL_pdf)
					: FinancialService.getNfsePDF(row?.nfse_id);

			call.then((result) => {
				setIsLoading(false);
				if (result instanceof Error) {
					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title="Erro" content="Erro ao buscar os detalhes da cobrança. Por favor, tente novamente." />,
					});
				}
			}).catch((err) => {
				setIsLoading(false);
				const { titleMessage, contentMessage } = returnMessageError(err);
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
				});
			});
		});
	};

	const handleUpdateStatusNfse = (nfse: NfseUI, refresh: () => void) => {
		setIsLoading(true);
		debounce(() => {
			FinancialService.updateNfseStatusGridWebti(nfse)
				.then((result: INfse | Error) => {
					setIsLoading(false);
					if (result instanceof Error) {
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title="Alerta!!" content={"Erro: " + result.message} />,
						});
					} else {
						refresh();
						toastHandler.present({
							type: "success",
							position: "top-right",
							messageOrContent: <ToastContent title="Sucesso" content="Processado a emissão!" />,
						});
					}
				})
				.catch((err: any) => {
					setIsLoading(false);
					refresh();
					const { titleMessage, contentMessage } = returnMessageError(err);
					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
					});
				});
		});
	};

	const handleReprocessNfse = async (nfse: NfseUI) => {
		const result = await FinancialService.updateNfseStatusGridWebti(nfse);

		if (result instanceof Error) {
			throw result;
		}

		return result;
	};

	const handleCancelStatusNfse = (nfse: NfseUI, refresh: () => void) => {
		setIsLoading(true);
		debounce(() => {
			FinancialService.updateStatusCancelNfseGridWebti(nfse.nfse_id, nfse.nfse_nfse_protocol)
				.then((result: any) => {
					setIsLoading(false);
					if (result instanceof Error) {
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title="Alerta!!" content={"Erro: " + result.message} />,
						});
					} else {
						refresh();
						toastHandler.present({
							type: "success",
							position: "top-right",
							messageOrContent: <ToastContent title="Sucesso" content="Verificado o status do cancelamento com sucesso!" />,
						});
					}
				})
				.catch((err: any) => {
					setIsLoading(false);
					refresh();
					const { titleMessage, contentMessage } = returnMessageError(err);
					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
					});
				});
		});
	};

	const handleGenerateNfse = async (idBillet: string) => {
		setIsLoading(true);

		try {
			await FinancialService.generateNfse(idBillet);
			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title="Sucesso" content="Nota gerada com sucesso" />,
			});
		} catch (err: any) {
			const { titleMessage, contentMessage } = returnMessageError(err);
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleCancelNfseByReason = (loadId: string, reasonCancel: string, documentGuid: string, refresh: () => void) => {
		setIsLoading(true);

		debounce(() => {
			FinancialService.cancelarNfseGridWebti(loadId, reasonCancel, documentGuid)
				.then((result) => {
					setIsLoading(false);

					if (result instanceof Error) {
						const titleMessage = "Alerta!!";
						const contentMessage = "Erro: " + result.message;

						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
						});
					} else {
						refresh();
						toastHandler.present({
							type: "success",
							position: "top-right",
							messageOrContent: <ToastContent title="Sucesso" content="Cancelamento solicitado com sucesso" />,
						});
					}
				})
				.catch((err: any) => {
					setIsLoading(false);
					refresh();
					const { titleMessage, contentMessage } = returnMessageError(err);

					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
					});
				});
		});
	};

	return {
		handleDownloadNfsePDF,
		handleUpdateStatusNfse,
		handleReprocessNfse,
		handleCancelStatusNfse,
		handleGenerateNfse,
		handleCancelNfseByReason,
		isLoading,
	};
}
